import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BedDouble, Tv, AirVent, Bath, Sparkles } from "lucide-react";
import { WhatsAppIcon, FadeUp, SectionHeading } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";
import { ROOM_IMAGES, waLink } from "../data/site";

const AMENITY_ICONS = [BedDouble, Tv, AirVent, Bath, Sparkles];

export default function Rooms() {
  const { T } = useLang();
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const count = T.rooms.items.length;

  const scrollToCard = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(count - 1, i));
    const child = track.children[clamped];
    if (child) track.scrollTo({ left: child.offsetLeft - 24, behavior: "smooth" });
  };

  const onTrackScroll = () => {
    const track = trackRef.current;
    if (!track || !track.children[0]) return;
    const step = track.children[0].offsetWidth + 16;
    setActive(Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / step))));
  };

  return (
    <section id="habitaciones" data-testid="rooms-section" className="py-14 md:py-24">
      <div className="container-x">
        <SectionHeading
          eyebrow={T.rooms.eyebrow}
          title={T.rooms.title}
          subtitle={T.rooms.subtitle}
        />

        <div className="relative mt-12 md:mt-14">
          <div
            ref={trackRef}
            onScroll={onTrackScroll}
            className="no-scrollbar -mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-3 md:mx-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 md:pb-0 lg:grid-cols-3"
          >
          {T.rooms.items.map((room, i) => (
            <FadeUp key={room.n} delay={(i % 3) * 0.08} className="w-[78vw] max-w-[340px] shrink-0 snap-center sm:w-[360px] md:w-auto md:max-w-none">
              <article
                data-testid={`room-card-${i + 1}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-federasol-terra/10 bg-white shadow-[0_14px_40px_-18px_rgba(64,49,47,0.14)] transition-all duration-500 ease-soft hover:-translate-y-2 hover:shadow-[0_30px_60px_-24px_rgba(166,93,71,0.32)]"
              >
                <div className="zoom-media relative h-56 overflow-hidden">
                  <img
                    src={ROOM_IMAGES[i]}
                    alt={room.n}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                  <span
                    data-testid={`room-capacity-chip-${i + 1}`}
                    className="absolute left-4 top-4 rounded-full border border-federasol-terra/20 bg-federasol-cream/95 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-federasol-terra backdrop-blur-sm"
                  >
                    {T.rooms.cap(room.cap)}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-serif text-2xl font-semibold text-federasol-cocoa">
                    {room.n}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-federasol-muted">
                    {T.rooms.desc}
                  </p>
                  <ul
                    className="mt-4 flex flex-wrap gap-1.5"
                    aria-label={T.rooms.amenitiesTitle}
                  >
                    {T.rooms.amenities.map((a, j) => {
                      const AIcon = AMENITY_ICONS[j];
                      return (
                        <li
                          key={a}
                          className="flex items-center gap-1.5 rounded-full bg-federasol-blush/80 px-3 py-1.5 text-[11px] font-medium text-federasol-cocoa"
                        >
                          <AIcon className="h-3 w-3 text-federasol-terra" strokeWidth={2} />
                          {a}
                        </li>
                      );
                    })}
                  </ul>
                  <a
                    href={waLink(T.wa.room(room.n))}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`room-whatsapp-btn-${i + 1}`}
                    className="btn-primary mt-6 w-full"
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                    {T.rooms.consult}
                  </a>
                </div>
              </article>
            </FadeUp>
          ))}
          </div>

          {/* Flechas móviles */}
          <button
            data-testid="rooms-carousel-prev"
            onClick={() => scrollToCard(active - 1)}
            disabled={active === 0}
            aria-label="Anterior"
            className={`absolute left-2 top-[38%] z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-federasol-terra/20 bg-federasol-cream/95 text-federasol-terra shadow-lg transition-opacity md:hidden ${active === 0 ? "opacity-0" : "opacity-100"}`}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            data-testid="rooms-carousel-next"
            onClick={() => scrollToCard(active + 1)}
            disabled={active === count - 1}
            aria-label="Siguiente"
            className={`absolute right-2 top-[38%] z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-federasol-terra/20 bg-federasol-cream/95 text-federasol-terra shadow-lg transition-opacity md:hidden ${active === count - 1 ? "opacity-0" : "opacity-100"}`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Puntos indicadores móviles */}
          <div data-testid="rooms-carousel-dots" className="mt-1 flex justify-center gap-2 md:hidden">
            {T.rooms.items.map((_, i) => (
              <button
                key={i}
                data-testid={`rooms-dot-${i}`}
                onClick={() => scrollToCard(i)}
                aria-label={`Ir a habitación ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ease-soft ${active === i ? "w-6 bg-federasol-rose" : "w-2 bg-federasol-rose/30"}`}
              />
            ))}
          </div>
        </div>

        <FadeUp delay={0.15} className="mt-8 md:mt-10">
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-5 rounded-3xl bg-federasol-blush/80 p-8 text-center sm:flex-row sm:justify-between sm:text-left">
            <p className="font-serif text-xl italic text-federasol-cocoa">
              {T.rooms.noteText}
            </p>
            <a
              href={waLink(T.wa.default)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rooms-general-whatsapp-btn"
              className="btn-whatsapp shrink-0"
            >
              <WhatsAppIcon className="h-4 w-4" />
              {T.rooms.noteCta}
            </a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
